import React from "react";
import dayjs, { Dayjs } from "dayjs";
import { DatePicker } from "antd";

import styles from "./DateRangeField.module.css";
import { getAdminNow } from "../../../../utils/dateService";

const { RangePicker } = DatePicker;

type DateRangePair = [string | null, string | null];
type DayjsPair = [Dayjs | null, Dayjs | null];

type Props = {
  value?: DateRangePair;
  onChange?: (value: DateRangePair) => void;
  /** When false the built-in quick presets are hidden (used by ScheduleField which has its own pill picks). Default true. */
  showPresets?: boolean;
  /** Disable the start half of the range (campaign already started). */
  lockStart?: boolean;
  /** Disable both ends (locked campaign schedule). */
  disabled?: boolean;
};

const DATE_TIME_FORMAT = "YYYY-MM-DD HH:mm";

/** ISO-8601 strings (form/store representation) → dayjs pair for the picker. */
const isoPairToDayjs = (v: DateRangePair | undefined): DayjsPair => {
  if (!v) return [null, null];
  return [v[0] ? dayjs(v[0]) : null, v[1] ? dayjs(v[1]) : null];
};

/** dayjs pair (picker output) → ISO-8601 strings for the backend contract. */
const dayjsPairToIso = (range: DayjsPair | null): DateRangePair => {
  const normalized: DayjsPair = range ?? [null, null];
  return [
    normalized[0]?.toISOString() ?? null,
    normalized[1]?.toISOString() ?? null,
  ];
};

/**
 * Quick-start presets relative to `base`. Campaigns are frequently short —
 * a 4-hour run is the common case — so we surface that as the first preset
 * alongside a day and a week.
 */
const buildSchedulePresets = (
  base: Dayjs,
): Array<{ label: string; value: [Dayjs, Dayjs] }> => [
  { label: "Next 4 hours", value: [base, base.add(4, "hour")] },
  { label: "Next 24 hours", value: [base, base.add(24, "hour")] },
  { label: "Next 7 days", value: [base, base.add(7, "day")] },
];

/**
 * Schedule picker for campaigns. Supports hour/minute granularity (not just
 * whole days) because short campaigns commonly run a few hours. Emits ISO-8601
 * strings so form values stay serializable and match the backend `datetime`
 * contract.
 */
const DateRangeField: React.FC<Props> = ({
  value,
  onChange,
  showPresets = true,
  lockStart = false,
  disabled = false,
}) => {
  return (
    <div className={styles.container}>
      <RangePicker
        value={isoPairToDayjs(value)}
        onChange={(range) => onChange?.(dayjsPairToIso(range as DayjsPair | null))}
        showTime={{ format: "HH:mm" }}
        format={DATE_TIME_FORMAT}
        presets={showPresets ? buildSchedulePresets(getAdminNow()) : []}
        placeholder={["Start date & time", "End date & time"]}
        disabled={disabled ? true : lockStart ? [true, false] : undefined}
        style={{ width: "100%" }}
      />
    </div>
  );
};

export { isoPairToDayjs, dayjsPairToIso, buildSchedulePresets };
export default DateRangeField;
